<template>
    <div class="px-16 py-8 w-full min-h-screen overflow-x-hidden flex flex-col bg-n-background text-n-slate-12">
        <div class="flex flex-col items-start w-full pt-4">
            <div class="flex items-center justify-between w-full gap-4">
                <h1 class="text-2xl font-semibold font-interDisplay tracking-[0.3px] text-slate-900 dark:text-slate-25">
                <woot-sidemenu-icon v-if="showSidemenuIcon" />
                Profissionais
                </h1>
                <button type="button"
                    class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-8 text-sm font-medium"
                    @click="openAddModal"
                    :disabled="activeTab === 'horarios'"
                    :class="{ 'opacity-50 cursor-not-allowed': activeTab === 'horarios' }">
                    <span class="i-lucide-plus flex-shrink-0"></span>
                    <span class="min-w-0 truncate">{{ addButtonText }}</span>
                </button>
            </div>

            <div class="flex flex-col w-full gap-3 text-slate-600 dark:text-slate-300">
                <p class="mb-0 text-base font-normal line-clamp-5 sm:line-clamp-none max-w-3xl tracking-[-0.1px] mt-[7px]">
                    Profissionais podem ser cadastrados junto com suas informações para integração com funcionalidades do sistema. Você pode organizá-los por especialidade.
                </p>
            </div>
        </div>

        <!-- Combined Tab Navigation / Bulk Actions Bar -->
        <div class="sticky top-0 z-50 w-full bg-n-solid-2 dark:bg-slate-800 border border-n-weak rounded-lg py-3 px-4 flex items-center justify-between transition-all duration-200 ease-in-out shadow-sm mb-6 mt-6">
            <!-- Default Mode - Shows when no items are selected -->
            <template v-if="selectedIds.length === 0">
                <!-- Tab Navigation - Left Side -->
                <div class="bg-n-solid-1 rounded-lg inline-flex border border-n-weak gap-1 max-h-8">
                    <button 
                        class="px-3 py-1.5 text-sm rounded-md flex items-center"
                        :class="activeTab === 'professionals' ? 'bg-n-brand text-white' : 'text-n-slate-11 hover:text-n-slate-12'"
                        @click="activeTab = 'professionals'"
                    >
                        <span class="i-lucide-users mr-1.5"></span>
                        Profissionais
                    </button>
                    <button 
                        class="px-3 py-1.5 text-sm rounded-md flex items-center"
                        :class="activeTab === 'specialties' ? 'bg-n-brand text-white' : 'text-n-slate-11 hover:text-n-slate-12'"
                        @click="activeTab = 'specialties'"
                    >
                        <span class="i-lucide-tags mr-1.5"></span>
                        Especialidades
                    </button>
                    <button 
                        class="px-3 py-1.5 text-sm rounded-md flex items-center"
                        :class="activeTab === 'horarios' ? 'bg-n-brand text-white' : 'text-n-slate-11 hover:text-n-slate-12'"
                        @click="activeTab = 'horarios'"
                    >
                        <span class="i-lucide-clock mr-1.5"></span>
                        Horários
                    </button>
                </div>
                
                <!-- Per-page dropdown - Right Side (only for professionals and specialties) -->
                <div v-if="activeTab !== 'horarios'" class="flex items-center gap-2 min-w-0">
                    <span class="text-sm font-medium text-n-slate-12 flex-shrink-0">
                        Por Página
                    </span>
                    <div class="relative flex items-center justify-end filter-actions min-w-0">
                        <div class="rounded-lg button-group outline-1 outline outline-n-container min-w-0">
                            <Button
                                :label="pagination.pageSize.toString()"
                                size="sm"
                                color="slate"
                                class="ltr:rounded-r-none rtl:rounded-l-none !outline-0 bg-n-background dark:bg-n-background min-w-0 flex-shrink"
                                @click="openPageSizeDropdown"
                            />
                            <Button
                                icon="i-lucide-chevron-down"
                                size="sm"
                                color="slate"
                                class="ltr:rounded-l-none rtl:rounded-r-none !outline-0 bg-n-background dark:bg-n-background"
                                trailing-icon
                                @click="openPageSizeDropdown"
                            />
                        </div>
                        <div
                            v-if="showPageSizeDropdown"
                            v-on-clickaway="closePageSizeDropdown"
                            class="dropdown-pane dropdown-pane--open left-auto top-full mt-0.5 ltr:right-0 rtl:left-0 max-w-[12.5rem] min-w-[9.75rem]"
                        >
                            <WootDropdownMenu class="mb-0">
                                <WootDropdownItem>
                                    <Button
                                        label="10"
                                        ghost
                                        slate
                                        sm
                                        start
                                        class="w-full"
                                        @click="() => selectPageSize(10)"
                                    />
                                </WootDropdownItem>
                                <WootDropdownItem>
                                    <Button
                                        label="25"
                                        ghost
                                        slate
                                        sm
                                        start
                                        class="w-full"
                                        @click="() => selectPageSize(25)"
                                    />
                                </WootDropdownItem>
                                <WootDropdownItem>
                                    <Button
                                        label="50"
                                        ghost
                                        slate
                                        sm
                                        start
                                        class="w-full"
                                        @click="() => selectPageSize(50)"
                                    />
                                </WootDropdownItem>
                                <WootDropdownItem>
                                    <Button
                                        label="100"
                                        ghost
                                        slate
                                        sm
                                        start
                                        class="w-full"
                                        @click="() => selectPageSize(100)"
                                    />
                                </WootDropdownItem>
                            </WootDropdownMenu>
                        </div>
                    </div>
                </div>
            </template>
            
            <!-- Selection Mode - Shows when 1 or more items are selected and not on horarios tab -->
            <template v-else-if="activeTab !== 'horarios'">
                <!-- Selection Count with Checkbox -->
                <div class="flex items-center gap-3">
                    <Checkbox 
                        v-model="selectAllChecked" 
                        :indeterminate="isSelectAllIndeterminate"
                        @change="toggleSelectAll"
                    />
                    <span class="text-sm font-medium text-n-slate-12">
                        {{ selectedIds.length }} {{ 
                            activeTab === 'professionals' ? (selectedIds.length === 1 ? 'profissional selecionado' : 'profissionais selecionados') : 
                            activeTab === 'specialties' ? (selectedIds.length === 1 ? 'especialidade selecionada' : 'especialidades selecionadas') :
                            (selectedIds.length === 1 ? 'item selecionado' : 'itens selecionados')
                        }}
                    </span>
                </div>
                
                <!-- Bulk Action Buttons -->
                <div class="flex gap-3">
                    <!-- Select All Button -->
                    <Button 
                        variant="faded" 
                        color="blue" 
                        size="sm"
                        icon="i-lucide-check-square"
                        @click="handleSelectAll"
                    >
                        <span class="min-w-0 truncate">{{ selectAllChecked ? 'Desmarcar todos' : 'Selecionar todos' }}</span>
                    </Button>
                    
                    <!-- Cancel Button -->
                    <Button 
                        variant="faded" 
                        color="slate" 
                        size="sm"
                        icon="i-lucide-x"
                        @click="clearSelection"
                    >
                        <span class="min-w-0 truncate">Cancelar</span>
                    </Button>
                    
                    <!-- Delete Selected Button -->
                    <Button 
                        variant="faded" 
                        color="ruby" 
                        size="sm"
                        icon="i-lucide-trash-2"
                        @click="handleBulkDelete"
                    >
                        <span class="min-w-0 truncate">Excluir selecionados</span>
                    </Button>
                </div>
            </template>
            
        </div>

        <!-- Content Area -->
        <div class="mt-5 p-0">
            <!-- Loading state -->
            <div v-if="loading" class="flex flex-col justify-center items-center px-24 py-12 rounded-lg text-center bg-n-slate-2 dark:bg-n-slate-800 text-n-slate-11 loading-state">
                <p>{{ 
                    activeTab === 'professionals' ? 'Carregando profissionais...' : 
                    activeTab === 'specialties' ? 'Carregando especialidades...' :
                    'Carregando horários...'
                }}</p>
            </div>

            <!-- Error state -->
            <div v-else-if="error" class="flex flex-col justify-center items-center px-24 py-12 rounded-lg text-center bg-n-ruby-9/5 dark:bg-n-ruby-9/10 text-n-slate-11">
                <p>{{ error }}</p>
                <button
                    class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-8 px-3 text-sm justify-center mt-3"
                    @click="retryLoad">
                    <span class="i-lucide-refresh-cw flex-shrink-0"></span>
                    <span>Tentar novamente</span>
                </button>
            </div>

            <!-- Empty state -->
            <div v-else-if="(activeTab === 'professionals' && professionals.length === 0) || (activeTab === 'specialties' && specialties.length === 0) || (activeTab === 'horarios' && professionals.length === 0)" class="flex flex-col justify-center items-center px-24 py-20 rounded-lg text-center bg-n-slate-2 dark:bg-n-slate-800 text-n-slate-11">
                <p v-if="activeTab === 'professionals'">Nenhum profissional adicionado. Clique em "Adicionar Profissional" para adicionar um.</p>
                <p v-else-if="activeTab === 'specialties'">Nenhuma especialidade adicionada. Clique em "Adicionar especialidade" para adicionar uma.</p>
                <p v-else>Nenhum profissional cadastrado. Cadastre profissionais para ver seus horários aqui.</p>
            </div>

            <!-- Data table (shown when there is data) -->
            <template v-else>
                <!-- Professional and Specialty tabs use table -->
                <div v-if="activeTab !== 'horarios'" class="w-full bg-white dark:bg-slate-900 rounded-md border border-n-weak overflow-hidden">
                    <TableMod 
                        :table="table" 
                        :truncate-cells="true" 
                        :max-cell-length="60" 
                        :exclude-from-truncate="['actions', 'selection']"
                        class="w-full" 
                        @row-click="handleRowClick"
                    />
                    <div class="p-4 border-t border-n-weak">
                        <PaginationList
                            :table="table"
                            :default-page-size="pagination.pageSize"
                            :show-page-size-selector="true"
                            :total="activeTab === 'professionals' ? professionalsServerMeta.total_count : specialtiesServerMeta.total_count"
                            @page-size-change="async (size) => { 
                                pagination.pageSize = size; 
                                pagination.pageIndex = 0;
                                if (activeTab === 'professionals') {
                                    await loadProfessionals();
                                } else if (activeTab === 'specialties') {
                                    await loadSpecialties();
                                }
                            }"
                        />
                    </div>
                </div>
                
                <!-- Horarios tab uses simple containers -->
                <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div 
                        v-for="professional in professionalWorkingHours" 
                        :key="professional.id"
                        class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-6 flex flex-col transition-all duration-200 hover:-translate-y-1 hover:outline-n-brand"
                    >
                        <div class="flex items-center justify-between mb-2">
                            <div class="flex items-center gap-3">
                                <div 
                                    class="w-4 h-4 rounded-full flex-shrink-0" 
                                    :style="{ backgroundColor: professional.color }"
                                ></div>
                                <h3 class="text-lg font-medium">{{ professional.name }}</h3>
                            </div>
                            <button class="px-2 py-1 text-xs font-medium rounded-full bg-n-solid-2 border border-n-brand text-n-brand hover:bg-n-brand hover:text-white transition-colors"
                                @click="openEditModal(professionals.find(p => p.id === professional.id), professionals.findIndex(p => p.id === professional.id))">
                                Ver mais
                            </button>
                        </div>
                        <p class="text-n-slate-11 mb-4">
                            {{ professional.status === 'Ativo' ? 'Horários de trabalho configurados' : 'Não há horários configurados para esse profissional' }}
                        </p>
                        
                        <!-- Working hours content -->
                        <div v-if="professional.status === 'Ativo'">
                            <h4 class="text-base font-medium text-n-slate-12 mb-3">Horários da semana</h4>
                            <div class="space-y-2">
                                <div 
                                    v-for="day in daysOfWeek" 
                                    :key="day.key"
                                    class="flex items-center justify-between text-sm p-2 rounded-lg"
                                    :class="professional.fullSchedule[day.key]?.schedule !== 'Não trabalha' ? 'bg-n-slate-2' : 'bg-n-slate-1'"
                                >
                                    <span class="font-medium text-n-slate-11 min-w-[40px]">{{ day.label }}</span>
                                    <span :class="professional.fullSchedule[day.key]?.schedule !== 'Não trabalha' ? 'text-n-slate-12' : 'text-n-slate-9 italic'">
                                        {{ professional.fullSchedule[day.key]?.schedule || 'Não trabalha' }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- No working hours content -->
                        <div v-else class="bg-n-slate-2 rounded-lg flex items-center justify-center min-h-48 flex-1">
                            <div class="flex flex-row items-center justify-center gap-3">
                                <span class="i-lucide-clock text-2xl text-n-slate-9"></span>
                                <p class="text-n-slate-10 text-base">Nenhum horário configurado</p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <!-- Professional Modal -->
        <GeneralProfessionalModal 
            v-if="showModal && (modalMode === 'add' || modalMode === 'edt' || modalMode === 'del')" 
            :mode="modalMode" 
            :professional="selectedProfessional" 
            :specialties="specialties"
            @close="hideModal" 
            @save="saveProfessional" 
            @update="updateProfessional" 
            @delete="deleteProfessional" 
        />
        
        <!-- Specialty Modal -->
        <GeneralSpecialtyModal 
            v-if="showModal && (modalMode === 'add-specialty' || modalMode === 'del-specialty')" 
            :mode="modalMode === 'add-specialty' ? 'add' : 'del'" 
            :specialty="selectedSpecialty" 
            @close="hideModal" 
            @save="saveSpecialty" 
            @delete="deleteSpecialty" 
        />
    </div>
</template>

<script>
import { ref, onMounted, computed, reactive, h, watch } from 'vue';
import { 
    createColumnHelper, 
    getCoreRowModel, 
    getSortedRowModel, 
    useVueTable 
} from '@tanstack/vue-table';
import GeneralProfessionalModal from './components/GeneralProfessionalModal.vue';
import GeneralSpecialtyModal from './components/GeneralSpecialtyModal.vue';
import TableMod from '@/dashboard/components/table/tableMod/TableMod.vue';
import TruncatedCell from '@/dashboard/components/table/tableMod/TruncatedCell.vue';
import PaginationList from '@/dashboard/components/table/tableMod/PaginationList.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import wootAPI from '@/v3/api/apiClient';

export default {
    name: 'AppointmentsProfessionals',
    components: {
        GeneralProfessionalModal,
        GeneralSpecialtyModal,
        TableMod,
        PaginationList,
        Button,
        Checkbox,
        WootDropdownMenu,
        WootDropdownItem
    },
    setup() {
        const activeTab = ref('professionals');
        const showModal = ref(false);
        const modalMode = ref('add');
        const professionals = ref([]);
        const selectedProfessionalIndex = ref(null);
        const selectedProfessional = ref(null);
        
        // Specialties state
        const specialties = ref([]);
        const selectedSpecialty = ref(null);
        
        // Selection state
        const selectedProfessionalIds = ref([]);
        const selectedSpecialtyIds = ref([]);
        const selectAllChecked = ref(false);
        
        // Page size dropdown state
        const showPageSizeDropdown = ref(false);

        const professionalsLoading = ref(true);
        const professionalsError = ref(null);
        const specialtiesLoading = ref(false);
        const specialtiesError = ref(null);
        
        // Computed properties for current tab
        const loading = computed(() => {
            if (activeTab.value === 'professionals') return professionalsLoading.value;
            if (activeTab.value === 'specialties') return specialtiesLoading.value;
            return professionalsLoading.value; // horarios tab uses professionals data
        });
        
        const error = computed(() => {
            if (activeTab.value === 'professionals') return professionalsError.value;
            if (activeTab.value === 'specialties') return specialtiesError.value;
            return professionalsError.value; // horarios tab uses professionals data
        });
        
        const addButtonText = computed(() => {
            return 'Adicionar';
        });
        
        // Selection computed properties
        const selectedIds = computed(() => {
            if (activeTab.value === 'professionals') return selectedProfessionalIds.value;
            if (activeTab.value === 'specialties') return selectedSpecialtyIds.value;
            return []; // horarios tab doesn't support selection
        });
        
        const currentData = computed(() => {
            if (activeTab.value === 'professionals') return professionals.value;
            if (activeTab.value === 'specialties') return specialties.value;
            return []; // horarios tab doesn't use table data
        });
        
        const isSelectAllIndeterminate = computed(() => {
            const currentPageIds = currentData.value.map(item => item.id);
            const selectedOnPage = currentPageIds.filter(id => selectedIds.value.includes(id));
            return selectedOnPage.length > 0 && selectedOnPage.length < currentPageIds.length;
        });

        // Configuração de ordenação
        const sorting = ref([]);
        
        // Pagination state
        const professionalsPagination = reactive({
            pageIndex: 0,
            pageSize: 10,
        });
        
        const specialtiesPagination = reactive({
            pageIndex: 0,
            pageSize: 10,
        });
        
        // Server pagination metadata for professionals
        const professionalsServerMeta = ref({
            total_count: 0,
            total_pages: 0,
            current_page: 1,
            per_page: 10
        });
        
        // Server pagination metadata for specialties
        const specialtiesServerMeta = ref({
            total_count: 0,
            total_pages: 0,
            current_page: 1,
            per_page: 10
        });
        
        const pagination = computed(() => {
            return activeTab.value === 'professionals' ? professionalsPagination : specialtiesPagination;
        });

        // Função para formatar o número de telefone
        const formatPhoneNumber = (phoneNumber) => {
            if (!phoneNumber) return '-';

            // Remove todos os caracteres não numéricos
            const cleanNumber = phoneNumber.replace(/\D/g, '');

            // Se o número não tiver dígitos suficientes, retorna o número original
            if (cleanNumber.length < 10) return phoneNumber || '-';

            const countryCode = cleanNumber.substring(0, 2);
            const areaCode = cleanNumber.substring(2, 4);
            const firstPart = cleanNumber.substring(4, 9);
            const secondPart = cleanNumber.substring(9);

            return `+${countryCode} (${areaCode}) ${firstPart} ${secondPart}`;
        };

        // Formatar categorias/especialidades para exibição
        const formatCategories = (categories) => {
            if (!categories || !Array.isArray(categories) || categories.length === 0) return '-';
            return categories.join(', ');
        };
        
        // Get professional color from meta_data using colorId
        const getProfessionalColor = (professional) => {
            // Define predefined colors matching GeneralProfessionalModal
            const predefinedColors = [
                { colorId: 11, name: 'Tomate', value: '#D50000' },
                { colorId: 4, name: 'Flamingo', value: '#E67C73' },
                { colorId: 6, name: 'Tangerina', value: '#F4511E' },
                { colorId: 5, name: 'Banana', value: '#F6BF26' },
                { colorId: 2, name: 'Sálvia', value: '#33B679' },
                { colorId: 10, name: 'Manjericão', value: '#0B8043' },
                { colorId: 1, name: 'Pavão', value: '#039BE5' },
                { colorId: 9, name: 'Mirtilo', value: '#3F51B5' },
                { colorId: 7, name: 'Lavanda', value: '#7986CB' },
                { colorId: 3, name: 'Uva', value: '#8E24AA' },
                { colorId: 8, name: 'Grafite', value: '#616161' }
            ];
            
            if (professional.meta_data) {
                // Check for new colorId system
                if (professional.meta_data.colorId) {
                    const color = predefinedColors.find(c => c.colorId === professional.meta_data.colorId);
                    if (color) {
                        return color.value;
                    }
                }
                // Fallback to old professional_color if exists
                if (professional.meta_data.professional_color) {
                    return professional.meta_data.professional_color;
                }
            }
            
            return '#039BE5'; // Default to Pavão color
        };
        
        // Working hours data processing
        const daysOfWeek = [
            { key: 'sunday', label: 'Dom' },
            { key: 'monday', label: 'Seg' },
            { key: 'tuesday', label: 'Ter' },
            { key: 'wednesday', label: 'Qua' },
            { key: 'thursday', label: 'Qui' },
            { key: 'friday', label: 'Sex' },
            { key: 'saturday', label: 'Sab' }
        ];
        
        const parseWorkingHours = (workingHoursString) => {
            if (!workingHoursString) return null;
            
            try {
                return JSON.parse(workingHoursString);
            } catch (error) {
                console.error('Error parsing working hours:', error);
                return null;
            }
        };
        
        const formatTimeSlots = (timeSlots) => {
            if (!timeSlots || !Array.isArray(timeSlots)) return '';
            
            return timeSlots.map(slot => {
                if (slot.startTime && slot.endTime) {
                    return `${slot.startTime} - ${slot.endTime}`;
                }
                return '';
            }).filter(slot => slot).join(', ');
        };
        
        const formatWorkingDays = (workingHours) => {
            if (!workingHours) return '';
            
            // Handle both nested and flat formats
            const hoursData = workingHours.workingHours || workingHours;
            if (!hoursData) return '';
            
            const activeDays = daysOfWeek.filter(day => 
                hoursData[day.key] && 
                hoursData[day.key].enabled
            );
            
            return activeDays.map(day => day.label).join(', ');
        };
        
        const getTimezoneLabel = (timezone) => {
            switch (timezone) {
                case 'America/Sao_Paulo':
                    return 'GMT-3';
                case 'America/Manaus':
                    return 'GMT-4';
                case 'America/Rio_Branco':
                    return 'GMT-5';
                default:
                    return 'GMT-3';
            }
        };
        
        const formatScheduleForDay = (dayData) => {
            if (!dayData || !dayData.enabled || !dayData.timeSlots) return 'Não trabalha';
            
            const slots = dayData.timeSlots.filter(slot => slot.startTime && slot.endTime);
            if (slots.length === 0) return 'Não trabalha';
            
            return slots.map(slot => `${slot.startTime} - ${slot.endTime}`).join(', ');
        };
        
        // Process professionals data into working hours format
        const professionalWorkingHours = computed(() => {
            return professionals.value.map(professional => {
                const workingHoursData = parseWorkingHours(
                    professional.meta_data?.working_hours
                );
                
                if (!workingHoursData) {
                    return {
                        id: professional.id,
                        name: professional.name,
                        color: getProfessionalColor(professional),
                        timezone: 'Não configurado',
                        activeDays: '0 de 7 dias',
                        status: 'Inativo',
                        schedule: 'Não configurado',
                        fullSchedule: {}
                    };
                }
                
                // Handle both nested and flat formats
                const hoursData = workingHoursData.workingHours || workingHoursData;
                const timeZone = workingHoursData.timeZone;
                const activeDaysCount = Object.values(hoursData || {}).filter(
                    day => day.enabled
                ).length;
                
                const fullSchedule = {};
                daysOfWeek.forEach(day => {
                    fullSchedule[day.key] = {
                        label: day.label,
                        schedule: formatScheduleForDay(hoursData?.[day.key])
                    };
                });
                
                return {
                    id: professional.id,
                    name: professional.name,
                    color: getProfessionalColor(professional),
                    timezone: timeZone ? getTimezoneLabel(timeZone) : 'GMT-3',
                    activeDays: `${activeDaysCount} de 7 dias`,
                    status: activeDaysCount > 0 ? 'Ativo' : 'Inativo',
                    schedule: formatWorkingDays(workingHoursData) || 'Não configurado',
                    fullSchedule
                };
            });
        });
        
        // Selection management functions
        const toggleItemSelection = (itemId, event) => {
            if (event) {
                event.stopPropagation();
            }
            
            const targetArray = activeTab.value === 'professionals' ? selectedProfessionalIds : selectedSpecialtyIds;
            const index = targetArray.value.indexOf(itemId);
            
            if (index === -1) {
                targetArray.value.push(itemId);
            } else {
                targetArray.value.splice(index, 1);
            }
            
            updateSelectAllState();
        };
        
        const isItemSelected = (itemId) => {
            return selectedIds.value.includes(itemId);
        };
        
        const updateSelectAllState = () => {
            if (currentData.value.length === 0) {
                selectAllChecked.value = false;
                return;
            }
            
            // Count how many items from current page are selected
            const currentPageSelectedCount = currentData.value.filter(item => 
                selectedIds.value.includes(item.id)
            ).length;
            
            selectAllChecked.value = currentPageSelectedCount === currentData.value.length;
        };
        
        const toggleSelectAll = () => {
            const targetArray = activeTab.value === 'professionals' ? selectedProfessionalIds : selectedSpecialtyIds;
            const currentPageIds = currentData.value.map(item => item.id);

            if (selectAllChecked.value) {
                // Add only missing current page IDs
                targetArray.value = [
                    ...new Set([...targetArray.value, ...currentPageIds])
                ];
            } else {
                // Remove only current page IDs
                targetArray.value = targetArray.value.filter(
                    id => !currentPageIds.includes(id)
                );
            }

            updateSelectAllState();
        };
        
        const clearSelection = () => {
            selectedProfessionalIds.value = [];
            selectedSpecialtyIds.value = [];
            selectAllChecked.value = false;
        };

        const selectAllProfessionals = async () => {
            try {
                const headers = getAuthHeaders();
                
                // Get all professionals from server
                const response = await wootAPI.get('/api/v1/professionals', {
                    headers,
                    params: {
                        page: 1,
                        per_page: 10000 // Large number to get all professionals
                    }
                });

                const { data: responseData } = response.data;
                
                if (responseData && responseData.length > 0) {
                    // Select all professional IDs
                    selectedProfessionalIds.value = responseData.map(professional => professional.id);
                    selectAllChecked.value = true;
                }
            } catch (err) {
                console.error('Failed to select all professionals:', err);
                // Fallback to current page selection if API fails
                selectedProfessionalIds.value = professionals.value.map(professional => professional.id);
                selectAllChecked.value = selectedProfessionalIds.value.length === professionals.value.length;
            }
        };

        const selectAllSpecialties = async () => {
            try {
                const headers = getAuthHeaders();
                
                // Get all specialties from server
                const response = await wootAPI.get('/api/v1/specialties', {
                    headers,
                    params: {
                        page: 1,
                        per_page: 10000 // Large number to get all specialties
                    }
                });

                const { data: responseData } = response.data;
                
                if (responseData && responseData.length > 0) {
                    // Select all specialty IDs
                    selectedSpecialtyIds.value = responseData.map(specialty => specialty.id);
                    selectAllChecked.value = true;
                }
            } catch (err) {
                console.error('Failed to select all specialties:', err);
                // Fallback to current page selection if API fails
                selectedSpecialtyIds.value = specialties.value.map(specialty => specialty.id);
                selectAllChecked.value = selectedSpecialtyIds.value.length === specialties.value.length;
            }
        };
        
        // Page size dropdown functions
        const openPageSizeDropdown = () => {
            showPageSizeDropdown.value = true;
        };
        
        const closePageSizeDropdown = () => {
            showPageSizeDropdown.value = false;
        };
        
        const selectPageSize = async (size) => {
            pagination.value.pageSize = size;
            pagination.value.pageIndex = 0;
            closePageSizeDropdown();
            
            // Refresh data for current tab
            if (activeTab.value === 'professionals') {
                await refreshProfessionals();
            } else {
                await refreshSpecialties();
            }
        };
        
        // Bulk actions functions
        const handlePageSizeChange = (event) => {
            const newSize = parseInt(event.target.value);
            pagination.value.pageSize = newSize;
            pagination.value.pageIndex = 0;
        };
        
        const handleSelectAll = async () => {
            if (selectAllChecked.value) {
                // If currently all selected, clear selection
                clearSelection();
            } else {
                // If not all selected, select all items
                if (activeTab.value === 'professionals') {
                    await selectAllProfessionals();
                } else {
                    await selectAllSpecialties();
                }
            }
        };
        
        const handleBulkDelete = () => {
            if (selectedIds.value.length === 0) return;
            
            const itemType = activeTab.value === 'professionals' ? 'profissionais' : 'especialidades';
            const confirmMessage = `Tem certeza que deseja excluir ${selectedIds.value.length} ${itemType} selecionados? Esta ação não pode ser desfeita.`;
            
            if (confirm(confirmMessage)) {
                bulkDeleteItems();
            }
        };
        
        const bulkDeleteItems = async () => {
            const headers = getAuthHeaders();
            if (!headers['access-token']) {
                alert('Usuário não autenticado.');
                return;
            }
            
            try {
                const promises = selectedIds.value.map(id => {
                    const endpoint = activeTab.value === 'professionals' 
                        ? `/api/v1/professionals/${id}`
                        : `/api/v1/specialties/${id}`;
                    return wootAPI.delete(endpoint, { headers });
                });
                
                await Promise.all(promises);
                
                // Remove deleted items from local data
                if (activeTab.value === 'professionals') {
                    professionals.value = professionals.value.filter(p => !selectedIds.value.includes(p.id));
                } else {
                    specialties.value = specialties.value.filter(s => !selectedIds.value.includes(s.id));
                }
                
                clearSelection();
                
                // Reload data to ensure consistency
                if (activeTab.value === 'professionals') {
                    await refreshProfessionals();
                } else {
                    await refreshSpecialties();
                }
                
            } catch (error) {
                console.error('Erro ao excluir itens:', error);
                alert('Erro ao excluir alguns itens. Tente novamente.');
            }
        };

        // Configuração da tabela usando TanStack Table
        const columnHelper = createColumnHelper();

        const professionalColumns = [
            // Checkbox column
            columnHelper.accessor('checkbox', {
                id: 'selection',
                header: () => {
                    return h(Checkbox, {
                        modelValue: selectAllChecked.value,
                        indeterminate: isSelectAllIndeterminate.value,
                        'onUpdate:modelValue': (value) => {
                            selectAllChecked.value = value;
                            toggleSelectAll();
                        },
                        onClick: (e) => e.stopPropagation(),
                        disabled: currentData.value.length === 0
                    });
                },
                cell: ({ row }) => {
                    return h(Checkbox, {
                        modelValue: isItemSelected(row.original.id),
                        onChange: (e) => {
                            toggleItemSelection(row.original.id, e);
                        },
                        onClick: (e) => e.stopPropagation(),
                    });
                },
                enableSorting: false,
                size: 50,
            }),
            columnHelper.accessor('name', {
                header: () => 'Nome',
                cell: info => {
                    const professional = info.row.original;
                    const professionalColor = getProfessionalColor(professional);
                    
                    return h('div', { class: 'flex items-center gap-2' }, [
                        h('div', {
                            class: 'w-3 h-3 rounded-full flex-shrink-0',
                            style: { backgroundColor: professionalColor }
                        }),
                        h(TruncatedCell, {
                            content: info.getValue() || '-',
                            maxLength: 40,
                            maxLines: 2,
                            class: 'text-n-slate-12',
                        })
                    ]);
                },
                enableSorting: true,
                minSize: 150,
            }),
            columnHelper.accessor(row => {
                return formatCategories(row.category);
            }, {
                id: 'category',
                header: () => 'Especialidades',
                cell: info => h(TruncatedCell, {
                    content: info.getValue(),
                    maxLength: 60,
                    maxLines: 2,
                    class: 'text-n-slate-12',
                }),
                enableSorting: true,
                minSize: 200,
            }),
            columnHelper.accessor(row => row.id, {
                id: 'actions',
                header: () => 'Ações',
                cell: info => {
                    const professional = info.row.original;
                    return h('div', { class: 'flex items-center space-x-2' }, [
                        h(Button, {
                            variant: 'ghost',
                            color: 'slate',
                            size: 'sm',
                            icon: 'i-lucide-edit-3',
                            onClick: event => {
                                event.stopPropagation();
                                openEditModal(professional, professionals.value.indexOf(professional));
                            }
                        }),
                        h(Button, {
                            variant: 'ghost',
                            color: 'ruby',
                            size: 'sm',
                            icon: 'i-lucide-trash-2',
                            onClick: event => {
                                event.stopPropagation();
                                openDeleteModal(professional, professionals.value.indexOf(professional));
                            }
                        })
                    ]);
                },
                enableSorting: false,
                minSize: 100,
                maxSize: 120,
            }),
        ];
        
        const specialtyColumns = [
            // Checkbox column
            columnHelper.accessor('checkbox', {
                id: 'selection',
                header: () => {
                    return h(Checkbox, {
                        modelValue: selectAllChecked.value,
                        indeterminate: isSelectAllIndeterminate.value,
                        'onUpdate:modelValue': (value) => {
                            selectAllChecked.value = value;
                            toggleSelectAll();
                        },
                        onClick: (e) => e.stopPropagation(),
                        disabled: currentData.value.length === 0
                    });
                },
                cell: ({ row }) => {
                    return h(Checkbox, {
                        modelValue: isItemSelected(row.original.id),
                        onChange: (e) => {
                            toggleItemSelection(row.original.id, e);
                        },
                        onClick: (e) => e.stopPropagation(),
                    });
                },
                enableSorting: false,
                size: 50,
            }),
            columnHelper.accessor('name', {
                header: () => 'Especialidade',
                cell: info => h(TruncatedCell, {
                    content: info.getValue() || '-',
                    maxLength: 40,
                    maxLines: 2,
                    class: 'text-n-slate-12',
                }),
                enableSorting: true,
                minSize: 200,
            }),
            columnHelper.accessor('description', {
                header: () => 'Descrição',
                cell: info => h(TruncatedCell, {
                    content: info.getValue() || '-',
                    maxLength: 60,
                    maxLines: 2,
                    class: 'text-n-slate-12',
                }),
                enableSorting: true,
                minSize: 300,
            }),
            columnHelper.accessor(row => row.id, {
                id: 'actions',
                header: () => 'Ações',
                cell: info => {
                    const specialty = info.row.original;
                    return h('div', { class: 'flex items-center space-x-2' }, [
                        h(Button, {
                            variant: 'ghost',
                            color: 'ruby',
                            size: 'sm',
                            icon: 'i-lucide-trash-2',
                            onClick: event => {
                                event.stopPropagation();
                                openDeleteSpecialtyModal(specialty, specialties.value.indexOf(specialty));
                            }
                        })
                    ]);
                },
                enableSorting: false,
                minSize: 100,
                maxSize: 120,
            }),
        ];
        
        
        const columns = computed(() => {
            if (activeTab.value === 'professionals') return professionalColumns;
            if (activeTab.value === 'specialties') return specialtyColumns;
            return []; // horários tab doesn't use table columns
        });

        // Calculate pageCount reactively
        const pageCount = computed(() => {
            if (activeTab.value === 'professionals') {
                return Math.ceil(professionalsServerMeta.value.total_count / pagination.value.pageSize);
            } else if (activeTab.value === 'specialties') {
                return Math.ceil(specialtiesServerMeta.value.total_count / pagination.value.pageSize);
            } else {
                return 1; // horarios tab doesn't use pagination
            }
        });

        // Get paginated data based on current pagination state
        const paginatedData = computed(() => {
            if (activeTab.value === 'professionals') {
                return professionals.value; // Use server-paginated data directly
            } else if (activeTab.value === 'specialties') {
                return specialties.value; // Use server-paginated data directly
            } else {
                return []; // horarios tab doesn't use table data
            }
        });
        
        // Implementing the table
        const table = computed(() => {
            return useVueTable({
                get data() {
                    return paginatedData.value;
                },
                columns: columns.value,
                state: {
                    get sorting() {
                        return sorting.value;
                    },
                    get pagination() {
                        return pagination.value;
                    }
                },
                onSortingChange: updater => {
                    sorting.value = typeof updater === 'function' ? updater(sorting.value) : updater;
                },
                onPaginationChange: async (updater) => {
                    const newPag = typeof updater === 'function'
                        ? updater({ ...pagination.value })
                        : updater;

                    // If neither page nor size changed, do nothing
                    if (newPag.pageIndex === pagination.value.pageIndex && 
                        newPag.pageSize === pagination.value.pageSize) {
                        return;
                    }

                    // Update pagination state BEFORE fetching
                    pagination.value.pageIndex = newPag.pageIndex;
                    pagination.value.pageSize = newPag.pageSize;

                    // For professionals and specialties, fetch from server
                    // For horarios, no need to fetch since it uses client-side pagination
                    if (activeTab.value === 'professionals') {
                        await loadProfessionals();
                    } else if (activeTab.value === 'specialties') {
                        await loadSpecialties();
                    }
                    // horarios tab doesn't need to fetch data as it uses client-side pagination
                },
                getCoreRowModel: getCoreRowModel(),
                getSortedRowModel: getSortedRowModel(),
                
                // Configure pagination
                manualPagination: activeTab.value !== 'horarios', // horarios doesn't use pagination
                get pageCount() {
                    return pageCount.value;
                },
                getRowId: row => row.id || row.name,
            });
        });

        // Handle row click
        const handleRowClick = (row) => {
            if (activeTab.value === 'professionals') {
                openEditModal(row.original, professionals.value.indexOf(row.original));
            }
            // For specialties, do nothing since we only have add/delete functionality
        };

        // modal management
        const openAddModal = () => {
            if (activeTab.value === 'professionals') {
                openAddProfessionalModal();
            } else {
                openAddSpecialtyModal();
            }
        };
        
        const openAddProfessionalModal = () => {
            modalMode.value = 'add';
            selectedProfessional.value = null;
            showModal.value = true;
        };
        
        const openAddSpecialtyModal = () => {
            modalMode.value = 'add-specialty';
            selectedSpecialty.value = null;
            showModal.value = true;
        };

        const openEditModal = (professional, index) => {
            modalMode.value = 'edt';
            selectedProfessional.value = { ...professional };
            selectedProfessionalIndex.value = index;
            showModal.value = true;
        };

        const openDeleteModal = (professional, index) => {
            modalMode.value = 'del';
            selectedProfessional.value = { ...professional, index };
            showModal.value = true;
        };

        const hideModal = () => {
            showModal.value = false;
            selectedProfessional.value = null;
            selectedProfessionalIndex.value = null;
        };

        const saveProfessional = async (professionalData) => {
            hideModal();
            await refreshProfessionals();
        };

        const updateProfessional = async (updatedData) => {
            hideModal();
            await refreshProfessionals();
        };

        const deleteProfessional = async () => {
            hideModal();
            await refreshProfessionals();
        };
        
        const openDeleteSpecialtyModal = (specialty, index) => {
            modalMode.value = 'del-specialty';
            selectedSpecialty.value = { ...specialty, index };
            showModal.value = true;
        };
        
        const saveSpecialty = async (specialtyData) => {
            hideModal();
            await refreshSpecialties();
        };
        
        const deleteSpecialty = async () => {
            hideModal();
            await refreshSpecialties();
        };

        // logica para fazer a chamada GET
        const getCookie = (name) => {
            const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
            return match ? decodeURIComponent(match[2]) : null;
        };

        const getAuthHeaders = () => {
            const cookie = getCookie('cw_d_session_info');
            if (!cookie) {
                console.error('cw_d_session_info cookie not found');
                return {};
            }
            try {
                const sessionInfo = JSON.parse(cookie);
                return {
                    'access-token': sessionInfo['access-token'],
                    'client': sessionInfo['client'],
                    'uid': sessionInfo['uid']
                };
            } catch (e) {
                console.error('Error parsing cw_d_session_info cookie:', e);
                return {};
            }
        };

        const loadProfessionals = async () => {
            professionalsLoading.value = true;
            professionalsError.value = null;

            try {
                const headers = getAuthHeaders();
                const page = professionalsPagination.pageIndex + 1; // Convert 0-based to 1-based
                const per_page = professionalsPagination.pageSize;
                
                const response = await wootAPI.get('/api/v1/professionals', {
                    headers,
                    params: {
                        page,
                        per_page
                    }
                });
                
                const { data, meta } = response.data;
                professionals.value = data || [];
                professionalsServerMeta.value = meta;
                
                // Update select all checkbox state after loading professionals
                updateSelectAllState();
            } catch (err) {
                professionalsError.value = 'Falha ao carregar profissionais. Por favor, tente novamente.';
                console.error(err);
            } finally {
                professionalsLoading.value = false;
            }
        };

        const retryLoadProfessionals = () => {
            loadProfessionals();
        };
        
        // Function to refresh professionals data while maintaining current pagination
        const refreshProfessionals = async () => {
            await loadProfessionals();
        };
        
        // Function to refresh specialties data while maintaining current pagination
        const refreshSpecialties = async () => {
            await loadSpecialties();
        };
        
        const loadSpecialties = async () => {
            specialtiesLoading.value = true;
            specialtiesError.value = null;

            try {
                const headers = getAuthHeaders();
                const page = specialtiesPagination.pageIndex + 1; // Convert 0-based to 1-based
                const per_page = specialtiesPagination.pageSize;
                
                const response = await wootAPI.get('/api/v1/specialties', {
                    headers,
                    params: {
                        page,
                        per_page
                    }
                });
                
                const { data, meta } = response.data;
                specialties.value = data || [];
                specialtiesServerMeta.value = meta;
                
                // Update select all checkbox state after loading specialties
                updateSelectAllState();
            } catch (err) {
                specialtiesError.value = 'Falha ao carregar especialidades. Por favor, tente novamente.';
                console.error(err);
            } finally {
                specialtiesLoading.value = false;
            }
        };
        
        const retryLoadSpecialties = () => {
            loadSpecialties();
        };
        
        const retryLoad = () => {
            if (activeTab.value === 'professionals') {
                retryLoadProfessionals();
            } else {
                retryLoadSpecialties();
            }
        };

        // Watch for tab changes to clear selections
        watch(activeTab, () => {
            // Clear selections when switching tabs
            clearSelection();
        });
        
        onMounted(() => {
            loadProfessionals();
            loadSpecialties(); // Load specialties on page load for professional modal
        });

        return {
            // tab navigation
            activeTab,
            addButtonText,
            // modal
            showModal,
            modalMode,
            selectedProfessional,
            selectedSpecialty,
            openAddModal,
            openAddProfessionalModal,
            openAddSpecialtyModal,
            openEditModal,
            openDeleteModal,
            openDeleteSpecialtyModal,
            hideModal,
            saveProfessional,
            saveSpecialty,
            updateProfessional,
            deleteProfessional,
            deleteSpecialty,
            // data
            professionals,
            specialties,
            loading,
            error,
            retryLoad,
            loadProfessionals,
            refreshProfessionals,
            retryLoadProfessionals,
            loadSpecialties,
            refreshSpecialties,
            retryLoadSpecialties,
            // formatadores
            formatPhoneNumber,
            formatCategories,
            getProfessionalColor,
            // table data
            table,
            pagination,
            handleRowClick,
            sorting,
            paginatedData,
            pageCount,
            // selection
            selectedProfessionalIds,
            selectedSpecialtyIds,
            selectedIds,
            selectAllChecked,
            isSelectAllIndeterminate,
            toggleItemSelection,
            isItemSelected,
            toggleSelectAll,
            clearSelection,
            // bulk actions
            handlePageSizeChange,
            handleSelectAll,
            handleBulkDelete,
            selectAllProfessionals,
            selectAllSpecialties,
            // page size dropdown
            showPageSizeDropdown,
            openPageSizeDropdown,
            closePageSizeDropdown,
            selectPageSize,
            // server pagination metadata
            professionalsServerMeta,
            specialtiesServerMeta,
            // working hours
            professionalWorkingHours,
            parseWorkingHours,
            formatTimeSlots,
            formatWorkingDays,
            getTimezoneLabel,
            formatScheduleForDay,
            daysOfWeek
        };
    }
}
</script>

<style scoped>

.loading-state {
    position: relative;
    overflow: hidden;
}

/* Dark mode styling for loading state */
:global(.dark) .loading-state {
    background-color: #1e293b; /* dark slate color for dark mode */
    color: #94a3b8; /* light slate color for text in dark mode */
}

/* Add this gradient animation for loading state */
.loading-state::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 200%;
    height: 100%;
    background: linear-gradient(90deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.6) 50%,
            rgba(255, 255, 255, 0) 100%);
    animation: loading-gradient 1.5s infinite;
}

/* Dark mode gradient */
:global(.dark) .loading-state::before {
    background: linear-gradient(90deg,
            rgba(30, 41, 59, 0) 0%,
            rgba(30, 41, 59, 0.6) 50%,
            rgba(30, 41, 59, 0) 100%);
}

@keyframes loading-gradient {
    0% {
        transform: translateX(-100%);
    }

    100% {
        transform: translateX(100%);
    }
}

</style>